import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './MyBookings.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCalendarDays, faCalendarCheck, faCheckCircle, faBan, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { Container, Alert } from 'react-bootstrap';
import BackButton from '../../components/BackButton/BackButton';
import bookingsService, {
  Booking,
  BookingAction,
  BookingsParams,
  BookingServiceOption,
  BookingPaymentStatus,
} from '../../services/bookingsService';

const PAGE_SIZE = 9;

type TabKey = 'upcoming' | 'completed' | 'cancelled';

interface TabConfig {
  key: TabKey;
  icon: IconDefinition;
  headerClass: string;
  titleKey: string;
  descKey: string;
  emptyKey: string;
  emptyDescKey: string;
  query: BookingsParams;
}

const TABS: TabConfig[] = [
  {
    key: 'upcoming',
    icon: faCalendarDays,
    headerClass: 'mb-tab-header-upcoming',
    titleKey: 'upcomingAppointments',
    descKey: 'upcomingAppointmentsDesc',
    emptyKey: 'noUpcomingBookings',
    emptyDescKey: 'noBookingsDesc',
    query: { status: 'upcoming', kind: 'booking', service: 'teleconsultation' },
  },
  {
    key: 'completed',
    icon: faCheckCircle,
    headerClass: 'mb-tab-header-completed',
    titleKey: 'completedAppointments',
    descKey: 'completedAppointmentsDesc',
    emptyKey: 'noUpcomingBookings',
    emptyDescKey: 'noBookingsDesc',
    query: { status: 'completed', service: 'teleconsultation' },
  },
  {
    key: 'cancelled',
    icon: faBan,
    headerClass: 'mb-tab-header-cancelled',
    titleKey: 'cancelledAppointments',
    descKey: 'cancelledAppointmentsDesc',
    emptyKey: 'noUpcomingBookings',
    emptyDescKey: 'noBookingsDesc',
    query: { status: 'cancelled', service: 'teleconsultation' },
  },
];

const ACTION_PRIORITY: BookingAction[] = ['pay', 'join', 'reschedule', 'cancel'];

const ACTION_LABEL_KEYS: Record<BookingAction, string> = {
  pay: 'completePayment',
  join: 'joinNow',
  reschedule: 'reschedule',
  cancel: 'cancelBooking',
};

const PAYMENT_LABEL_KEYS: Partial<Record<BookingPaymentStatus, string>> = {
  paid: 'paid',
  refunded: 'refunded',
};

const SERVICE_LABEL_KEY_OVERRIDES: Record<string, string> = {
  voucher: 'offerServiceLabel',
};

const BOOKING_DATE_LABEL_KEYS: Record<string, string> = {
  teleconsultation: 'appointmentDate',
  wellness: 'eventDate',
};

const BUCKET_COLORS: Record<string, { color: string; background: string }> = {
  upcoming: { color: '#0891b2', background: 'rgba(8, 145, 178, 0.08)' },
  completed: { color: '#059669', background: 'rgba(5, 150, 105, 0.08)' },
  cancelled: { color: '#f43f5e', background: 'rgba(244, 63, 94, 0.08)' },
};

const PENDING_COLORS = { color: '#d97706', background: 'rgba(217, 119, 6, 0.10)' };

const formatWhen = (iso: string | null, hasTime?: boolean): { date: string; time: string } => {
  if (!iso) {
    return { date: '', time: '' };
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return { date: '', time: '' };
  }
  const isMidnight = parsed.getHours() === 0 && parsed.getMinutes() === 0;
  const showTime = hasTime === undefined ? !isMidnight : hasTime;
  return {
    date: parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }),
    time: showTime ? parsed.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '',
  };
};

const formatDay = (iso: string | null): string => {
  const parsed = iso ? new Date(iso) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatAmount = (amount: string | null, currency: string): string => {
  if (amount === null || amount === '') {
    return '';
  }
  const value = Number(amount);
  if (Number.isNaN(value)) {
    return '';
  }
  const rendered = value.toLocaleString('en-IN');
  return currency === 'INR' || !currency ? `\u20B9${rendered}` : `${currency} ${rendered}`;
};

const primaryAction = (actions: BookingAction[]): BookingAction | null =>
  ACTION_PRIORITY.find((candidate) => actions.includes(candidate)) ?? null;

const emptyCounts = (): Record<TabKey, number> => ({
  upcoming: 0,
  completed: 0,
  cancelled: 0,
});

const MyBookings: React.FC = () => {
  const { t } = useTranslation(['services']);
  const [activeTab, setActiveTab] = useState<TabKey>('upcoming');
  const [serviceFilter, setServiceFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<Booking[]>([]);
  const [total, setTotal] = useState(0);
  const [services, setServices] = useState<BookingServiceOption[]>([]);
  const [counts, setCounts] = useState<Record<TabKey, number>>(emptyCounts);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentHeader = TABS.find((tab) => tab.key === activeTab) ?? TABS[0];

  const loadCounts = useCallback(async () => {
    try {
      const pages = await Promise.all(
        TABS.map((tab) =>
          bookingsService.getBookings({ ...tab.query, page_size: 1 }).then(
            (data) => ({ key: tab.key, data }),
          ),
        ),
      );
      const next = emptyCounts();
      pages.forEach(({ key, data }) => {
        next[key] = data.count;
      });
      setCounts(next);
      const directory = pages.find(({ data }) => data.services.length)?.data.services;
      if (directory) {
        setServices(directory);
      }
    } catch {
      setCounts(emptyCounts());
    }
  }, []);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bookingsService.getBookings({
        ...currentHeader.query,
        page,
        page_size: PAGE_SIZE,
      });
      setRows(data.results);
      setTotal(data.count);
      if (data.services.length) {
        setServices(data.services);
      }
      setError('');
    } catch {
      setRows([]);
      setTotal(0);
      setError(t('services:bookings.failedToLoadBookings'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, serviceFilter, t]);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const changeTab = (key: TabKey) => {
    setActiveTab(key);
    setPage(1);
  };

  const changeService = (value: string) => {
    setServiceFilter(value);
    setPage(1);
  };

  const tabLabel = t(`services:bookings.${activeTab}`);

  const serviceLabel = (key: string, fallback: string) => {
    const overrideKey = SERVICE_LABEL_KEY_OVERRIDES[key];
    return overrideKey ? t(`services:bookings.${overrideKey}`) : fallback;
  };

  const heroTotal = counts.upcoming + counts.completed + counts.cancelled;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const renderCard = (booking: Booking, index: number) => {
    const when = formatWhen(booking.date, booking.date_has_time);
    const accent =
      booking.status === 'pending'
        ? PENDING_COLORS
        : BUCKET_COLORS[booking.bucket];
    const action = primaryAction(booking.actions);
    const paymentLabelKey = PAYMENT_LABEL_KEYS[booking.payment_status];
    const amountText = formatAmount(booking.amount, booking.currency);

    let whenLabel = t('services:bookings.date');
    let whenValue = when.time ? `${when.date} \u00B7 ${when.time}` : when.date;
    if (booking.kind === 'entitlement') {
      whenLabel = booking.end_date ? t('services:bookings.validTill') : t('services:bookings.startedOn');
      whenValue = formatDay(booking.end_date || booking.date);
    } else {
      const dateLabelKey = BOOKING_DATE_LABEL_KEYS[booking.service];
      if (dateLabelKey) {
        whenLabel = t(`services:bookings.${dateLabelKey}`);
      }
    }

    return (
      <div
        className={`mb-booking-card mb-card-${booking.bucket}`}
        key={booking.id}
        style={{ animationDelay: `${index * 0.05}s` }}
      >
        <div className="mb-card-top">
          <span className="mb-case-id">{booking.reference}</span>
          <span
            className="mb-status-badge"
            style={{ color: accent.color, background: accent.background }}
          >
            {booking.status_label}
          </span>
        </div>
        <div className="mb-card-divider" />
        <div className="mb-card-info">
          <div className="mb-info-row">
            <span className="mb-info-label">{t('services:bookings.service')}</span>
            <span className="mb-info-value">{serviceLabel(booking.service, booking.service_label)}</span>
          </div>
          {booking.participant_name && (
            <div className="mb-info-row">
              <span className="mb-info-label">{t('services:bookings.patient')}</span>
              <span className="mb-info-value">{booking.participant_name}</span>
            </div>
          )}
          <div className="mb-info-row">
            <span className="mb-info-label">{t('services:bookings.type')}</span>
            <span className="mb-info-value">{booking.title}</span>
          </div>
          {whenValue && (
            <div className="mb-info-row">
              <span className="mb-info-label">{whenLabel}</span>
              <span className="mb-info-value">{whenValue}</span>
            </div>
          )}
          {amountText && (
            <div className="mb-info-row">
              <span className="mb-info-label">{t('services:bookings.amount')}</span>
              <span className="mb-info-value">
                <span className="mb-amount">{amountText}</span>
                {paymentLabelKey && (
                  <span className="mb-payment-chip">
                    {t(`services:bookings.${paymentLabelKey}`)}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="mb-card-actions">
          <Link to={booking.detail_url} className="mb-cta-btn">
            {action
              ? t(`services:bookings.${ACTION_LABEL_KEYS[action]}`)
              : t('services:bookings.viewDetails')}
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="mb-page">
      <section className="mb-hero-section">
        <div className="mb-hero-bg-circle mb-hero-bg-circle-1" />
        <div className="mb-hero-bg-circle mb-hero-bg-circle-2" />
        <Container>
          <BackButton />
          <div className="mb-hero-content">
            <div className="mb-hero-text">
              <div className="mb-hero-badge">
                <span className="mb-hero-badge-icon"><FontAwesomeIcon icon={faCalendarDays} /></span>
                {t('services:bookings.appointmentManager')}
              </div>
              <h1 className="mb-hero-title">
                <FontAwesomeIcon icon={faCalendarCheck} /> {t('services:bookings.title')}
              </h1>
              <p className="mb-hero-subtitle">{t('services:bookings.subtitle')}</p>
              <div className="mb-hero-stats">
                <div className="mb-hero-stat">
                  <span className="mb-hero-stat-icon"><FontAwesomeIcon icon={faCalendarDays} /></span>
                  <span className="mb-hero-stat-value">{heroTotal}</span>
                  <span className="mb-hero-stat-label">
                    {t('services:bookings.totalBookings')}
                  </span>
                </div>
                <div className="mb-hero-stat">
                  <span className="mb-hero-stat-icon"><FontAwesomeIcon icon={faCalendarDays} /></span>
                  <span className="mb-hero-stat-value">{counts.upcoming}</span>
                  <span className="mb-hero-stat-label">{t('services:bookings.upcoming')}</span>
                </div>
                <div className="mb-hero-stat">
                  <span className="mb-hero-stat-icon"><FontAwesomeIcon icon={faCheckCircle} /></span>
                  <span className="mb-hero-stat-value">{counts.completed}</span>
                  <span className="mb-hero-stat-label">{t('services:bookings.completed')}</span>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <Container>
        <div className="mb-tabs-container">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`mb-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => changeTab(tab.key)}
            >
              <span className="mb-tab-icon"><FontAwesomeIcon icon={tab.icon} /></span>
              {t(`services:bookings.${tab.key}`)}
              <span className="mb-tab-count">{counts[tab.key]}</span>
            </button>
          ))}
        </div>

        {services.length > 1 && false && (
          <div className="mb-filter-row">
            <label className="mb-filter-label" htmlFor="mb-service-filter">
              {t('services:bookings.allServices')}
            </label>
            <select
              id="mb-service-filter"
              className="mb-service-filter"
              value={serviceFilter}
              onChange={(event) => changeService(event.target.value)}
            >
              <option value="">{t('services:bookings.allServices')}</option>
              {services.map((option) => (
                <option key={option.key} value={option.key}>
                  {serviceLabel(option.key, option.label)}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <Alert variant="warning" className="mb-3" style={{ borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        <div className="mb-tab-content">
          <div className={`mb-tab-header ${currentHeader.headerClass}`}>
            <div className="mb-tab-header-text">
              <h2>{t(`services:bookings.${currentHeader.titleKey}`)}</h2>
              <p>{t(`services:bookings.${currentHeader.descKey}`)}</p>
            </div>
          </div>

          {loading ? (
            <div className="mb-loading">
              <FontAwesomeIcon icon={faSpinner} spin className="mb-spinner" />
              <p>{t('services:bookings.loadingBookings')}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="mb-empty-state">
              <div className="mb-empty-icon"><FontAwesomeIcon icon={currentHeader.icon} /></div>
              <h3>
                {t(`services:bookings.${currentHeader.emptyKey}`, {
                  tab: tabLabel,
                })}
              </h3>
              <p>{t(`services:bookings.${currentHeader.emptyDescKey}`, { tab: tabLabel })}</p>
            </div>
          ) : (
            <>
              <div className="mb-bookings-grid">
                {rows.map((booking, index) => renderCard(booking, index))}
              </div>

              {totalPages > 1 && (
                <div className="mb-pagination-row">
                  <button
                    type="button"
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="mb-pagination-btn"
                  >
                    {t('services:bookings.previous')}
                  </button>
                  <span className="mb-pagination-status">
                    {t('services:bookings.pageOf', { page, total: totalPages })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage(page + 1)}
                    disabled={page === totalPages}
                    className="mb-pagination-btn"
                  >
                    {t('services:bookings.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default MyBookings;
